/** AI Import — Google Places matching. The extract step places every activity
 *  (standalone or tour-embedded) in a geographic area and looks it up on Google
 *  INSIDE that area; the first in-area candidate is the default pick, seeded by
 *  the review page on load (`seedPlaceMatch`). This dialog lets the reviewer
 *  change an activity's area (when its tour has more than one), change the
 *  place (type to re-search, biased to the area) or clear it (no association).
 *  Applied picks ride along with the import approval as `place_selections`;
 *  the importer then fetches place details and augments the matched activities.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as aiImportApi from '@/api/aiImport';
import type {
  AiImportPlaceSuggestions,
  AiPlaceActivity,
  AiPlaceArea,
  AiPlaceCandidate,
} from '@/api/aiImport';
import { AppDialog } from '@/components/AppDialog';
import { HelpLink } from '@/components/HelpLink';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';

export type PlacePicks = Record<string, AiPlaceCandidate | null>;

/** The reviewer's matching choices, keyed by activity key. */
export interface PlaceMatchState {
  picks: PlacePicks;
  /** The area each activity is matched in (null = not placed). */
  areas: Record<string, string | null>;
}

/** The defaults: every activity in the area the extract step chose, matched to
 *  its first in-area candidate. */
export function seedPlaceMatch(data: AiImportPlaceSuggestions): PlaceMatchState {
  const picks: PlacePicks = {};
  const areas: Record<string, string | null> = {};
  for (const act of data.activities) {
    picks[act.key] = act.candidates.find((c) => c.place_id === act.suggested_place_id) ?? null;
    areas[act.key] = act.area_id;
  }
  return { picks, areas };
}

function candidateLabel(c: AiPlaceCandidate): string {
  if (c.name && c.formatted_address) return `${c.name} — ${c.formatted_address}`;
  return c.name ?? c.formatted_address ?? c.place_id;
}

export function areaLabel(a: AiPlaceArea): string {
  return a.country_codes.length ? `${a.label} (${a.country_codes.join(', ')})` : a.label;
}

const candidatesKey = (taskId: string, key: string, areaId: string) => [
  'ai-import',
  'place-candidates',
  taskId,
  key,
  areaId,
];

function PlaceRow({
  taskId,
  activity,
  groupAreas,
  areaId,
  value,
  onChange,
  onAreaChange,
}: {
  taskId: string;
  activity: AiPlaceActivity;
  /** The areas of the activity's tour (empty on tasks staged before areas existed). */
  groupAreas: AiPlaceArea[];
  areaId: string | null;
  value: AiPlaceCandidate | null;
  onChange: (v: AiPlaceCandidate | null) => void;
  onAreaChange: (areaId: string) => void;
}) {
  const queryClient = useQueryClient();
  const moved = areaId !== null && areaId !== activity.area_id;
  // In the extract step's own area the stored candidates apply; in another one,
  // the re-lookup (cached per activity + area, so toggling back costs nothing).
  const relookup = useQuery({
    queryKey: candidatesKey(taskId, activity.key, areaId ?? ''),
    queryFn: () => aiImportApi.placeCandidates(taskId, activity.key, areaId ?? ''),
    enabled: moved,
    staleTime: Infinity,
  });
  const candidates = useMemo(
    () => (moved ? (relookup.data?.candidates ?? []) : activity.candidates),
    [moved, relookup.data, activity.candidates],
  );

  // Empty query → the area's candidates; typing → live search biased to the area.
  const queryFn = useCallback(
    async (q: string, signal: AbortSignal): Promise<AiPlaceCandidate[]> => {
      if (!q.trim()) return candidates;
      const res = await aiImportApi.placeSearch(taskId, q, signal, areaId);
      return res.candidates;
    },
    [taskId, candidates, areaId],
  );

  // A changed area re-matches the activity: its pick becomes the new area's
  // first candidate. Only the latest change may land (the reviewer can switch
  // twice before the first lookup answers).
  const latestArea = useRef(areaId);
  const changeArea = async (next: string) => {
    latestArea.current = next;
    onAreaChange(next);
    if (next === activity.area_id) {
      onChange(activity.candidates.find((c) => c.place_id === activity.suggested_place_id) ?? null);
      return;
    }
    try {
      const res = await queryClient.fetchQuery({
        queryKey: candidatesKey(taskId, activity.key, next),
        queryFn: () => aiImportApi.placeCandidates(taskId, activity.key, next),
        staleTime: Infinity,
      });
      if (latestArea.current === next) onChange(res.candidates[0] ?? null);
    } catch {
      if (latestArea.current === next) onChange(null);
    }
  };

  // With one area there is nothing to change — unless the activity was left
  // unplaced, when that one area is still a choice.
  const areaSelectable = groupAreas.length > 1 || (areaId === null && groupAreas.length === 1);
  const hint = !activity.is_place
    ? 'not a findable place'
    : areaId === null && groupAreas.length > 0
      ? 'no area found — pick one, or search'
      : activity.query;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ md: 'center' }}
      sx={{ py: 1 }}
    >
      <Box sx={{ flex: '0 0 30%', minWidth: 0 }}>
        <Typography variant="body2" noWrap title={activity.name}>
          {activity.name || '(unnamed activity)'}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary" noWrap title={hint}>
            {hint}
          </Typography>
        )}
      </Box>
      {groupAreas.length > 0 && (
        <TextField
          select
          size="small"
          label="Area"
          value={areaId ?? ''}
          onChange={(e) => void changeArea(e.target.value)}
          disabled={!areaSelectable}
          sx={{ flex: '0 0 26%', minWidth: 0 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          {areaId === null && (
            <MenuItem value="" disabled>
              <em>No area</em>
            </MenuItem>
          )}
          {groupAreas.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {areaLabel(a)}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {moved && relookup.isFetching ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Looking up places in this area…
            </Typography>
          </Stack>
        ) : (
          <RemoteAutocomplete<AiPlaceCandidate>
            queryFn={queryFn}
            getOptionLabel={candidateLabel}
            getOptionKey={(o) => o.place_id}
            value={value}
            onChange={(v) => onChange((v as AiPlaceCandidate | null) ?? null)}
            fetchOnEmpty
            size="small"
            placeholder={
              candidates.length
                ? `No place — ${candidates.length} candidate(s), or search`
                : 'No place — search to match'
            }
          />
        )}
      </Box>
    </Stack>
  );
}

interface Section {
  id: string;
  name: string;
  areas: AiPlaceArea[];
  activities: AiPlaceActivity[];
}

/** One section per tour (plus the standalone activities); tasks staged before
 *  areas existed have no groups and render as one flat section. */
function sections(data: AiImportPlaceSuggestions): Section[] {
  const byGroup = new Map<string, Section>(
    data.groups.map((g) => [g.id, { id: g.id, name: g.name, areas: [], activities: [] }]),
  );
  for (const a of data.areas) byGroup.get(a.group_id)?.areas.push(a);
  const loose: AiPlaceActivity[] = [];
  for (const act of data.activities) {
    const section = act.group_id ? byGroup.get(act.group_id) : undefined;
    if (section) section.activities.push(act);
    else loose.push(act);
  }
  const out = [...byGroup.values()].filter((s) => s.activities.length > 0);
  if (loose.length) out.push({ id: '', name: '', areas: [], activities: loose });
  return out;
}

export function PlaceMatchDialog({
  open,
  onClose,
  taskId,
  data,
  loading,
  error,
  initial,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  taskId: string;
  data: AiImportPlaceSuggestions | undefined;
  loading: boolean;
  error: boolean;
  /** The applied choices (the seeded defaults until the reviewer applies changes). */
  initial: PlaceMatchState | null;
  onApply: (state: PlaceMatchState) => void;
}) {
  const [state, setState] = useState<PlaceMatchState>({ picks: {}, areas: {} });

  // Start from the applied choices each time the dialog opens; Cancel discards.
  useEffect(() => {
    if (open && data) setState(initial ?? seedPlaceMatch(data));
  }, [open, data, initial]);

  const grouped = useMemo(() => (data ? sections(data) : []), [data]);
  const activities = data?.activities ?? [];
  const matchedCount = Object.values(state.picks).filter(Boolean).length;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <>
          Match activities to Google Places <HelpLink story="ai-import" newTab />
        </>
      }
      actions={
        <>
          <Button color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onApply(state);
              onClose();
            }}
            disabled={loading || error || !data}
          >
            Apply{matchedCount ? ` (${matchedCount} matched)` : ''}
          </Button>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each activity was placed in a geographic area and matched to the first Google Place found
        inside it. Changing an activity&apos;s area looks it up again there. Each matched activity
        is enriched at import with the place&apos;s address, coordinates, rating, opening hours and
        photos (existing extracted values are kept). Clear a row to leave an activity without a
        place.
      </Typography>

      {loading && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading place suggestions…
          </Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error">Couldn’t load place suggestions. Close and try again.</Alert>
      )}

      {data && data.truncated && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Showing the first {activities.length} activities — matching is capped per run.
        </Alert>
      )}

      {data && activities.length === 0 && !loading && (
        <Alert severity="info">No activities in this import to match.</Alert>
      )}

      {grouped.map((section) => (
        <Box key={section.id || 'activities'} sx={{ mb: 2 }}>
          {section.name && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">{section.name}</Typography>
              {section.areas.map((a) => (
                <Chip key={a.id} size="small" variant="outlined" label={areaLabel(a)} />
              ))}
              {section.areas.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  no geographic area found
                </Typography>
              )}
            </Stack>
          )}
          <Stack divider={<Divider flexItem />}>
            {section.activities.map((act) => (
              <PlaceRow
                key={act.key}
                taskId={taskId}
                activity={act}
                groupAreas={section.areas}
                areaId={act.key in state.areas ? (state.areas[act.key] ?? null) : act.area_id}
                value={state.picks[act.key] ?? null}
                onChange={(v) =>
                  setState((prev) => ({ ...prev, picks: { ...prev.picks, [act.key]: v } }))
                }
                onAreaChange={(areaId) =>
                  setState((prev) => ({ ...prev, areas: { ...prev.areas, [act.key]: areaId } }))
                }
              />
            ))}
          </Stack>
        </Box>
      ))}
    </AppDialog>
  );
}
